import Car from "@/assets/icons/Car";
import Mail from "@/assets/icons/Mail";
import SpeechBubble from "@/assets/icons/SpeechBubble";
import Screen from "@/components/layout/Screen";
import ContactModal from "@/components/modals/ContactModal";
import AppButton from "@/components/shared/AppButton";
import { Card } from "@/components/shared/Card";
import PageTitle from "@/components/shared/PageTitle/PageTitle";
import UpdatePlateModal from "@/components/shared/UpdatePlateModal";
import { Box } from "@/components/ui/box";
import { Divider } from "@/components/ui/divider";
import { Heading } from "@/components/ui/heading";
import { HStack } from "@/components/ui/hstack";
import { Skeleton, SkeletonText } from "@/components/ui/skeleton";
import { Text } from "@/components/ui/text";
import { VStack } from "@/components/ui/vstack";
import { useCustomTheme } from "@/hooks/useCustomTheme";
import { useReceipt } from "@/hooks/useReceipt";
import { useSendReceipt } from "@/hooks/useSendReceipt";
import { updatePlate } from "@/services/plate.queries";
import { useToastNotify } from "@/stores/toastStore";
import { formatToDollars, prettyPassID } from "@/utils/etc";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { T, useT } from "@transifex/react";
import { useLocalSearchParams } from "expo-router";
import type { ReceiptMini } from "ipctg-inflow-types/InFlowDB";
import { PlateUpdateFailReason, ReceiptType } from "ipctg-inflow-types/InFlowEnums";
import type { ParkPlateUpdatePOSTErrorResponse } from "ipctg-inflow-types/InFlowParkAPI";
import { type ReactNode, useState } from "react";
import QRCode from "react-native-qrcode-svg";

const Section = ({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) => (
  <Box className="flex flex-col gap-[12px]">
    <Divider className="bg-outline-300" />
    <Box className="pt-[12px] gap-[12px]">
      <Heading className="text-typography-900 text-[16px] font-[700] leading-[22px] align-middle">{title}</Heading>
      <Box className="flex flex-col gap-[6px]">{children}</Box>
    </Box>
  </Box>
);

const DetailRow = ({ label, value }: { label: string; value: string }) => (
  <HStack space="sm" className="justify-between">
    <Text className="text-typography-900 text-[14px] font-[400] leading-[20px] align-middle text-left">{label}</Text>
    <Text className="text-typography-900 text-[14px] font-[400] leading-[20px] align-middle text-right break-words">{value}</Text>
  </HStack>
);

const Receipt = () => {
  const { receiptID } = useLocalSearchParams<{ receiptID: string }>();
  const { sendReceiptByEmail, sendReceiptByPhoneNumber, sendReceiptByEmailIsPending, sendReceiptByPhoneNumberIsPending } = useSendReceipt();
  const { notify } = useToastNotify();
  const [isPlateModalOpen, setIsPlateModalOpen] = useState<boolean>(false);
  const [isUpdatingPlate, setIsUpdatingPlate] = useState<boolean>(false);
  const queryClient = useQueryClient();
  const t = useT();
  const { skin } = useCustomTheme();

  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"email" | "phone" | null>(null);

  if (typeof receiptID !== "string") {
    throw new Error("wrong receiptID type");
  }

  const { receipt, formattedDate, formattedEndTime, formattedStartTime, isPending, parsedAllowPlateSwitchUntil } = useReceipt(receiptID || "");

  const getErrorMessage = (error: ParkPlateUpdatePOSTErrorResponse) => {
    if (error?.failReason) {
      switch (error.failReason) {
        case PlateUpdateFailReason.PlateNotSimilar:
          return t("Only minor corrections are allowed. The new plate is too different from the original to be updated.");
        case PlateUpdateFailReason.WindowClosed:
          return t("The license plate may no longer be changed.");
        case PlateUpdateFailReason.AlreadyPaidPlateSwitch:
          return t("A parking pass has already been activated for the submitted plate.");
        case PlateUpdateFailReason.SessionEnforced:
          return t("This plate is currently in violation status. Plate switching is no longer available.");
        case PlateUpdateFailReason.NotEligible:
          return t("This plate is not eligible for plate switching.");
        case PlateUpdateFailReason.InvalidPlate:
          return t("The submitted plate is invalid.");
        default:
          return t("An error occurred, please try again or contact support if the problem persists.");
      }
    }
    return t("An error occurred, please try again or contact support if the problem persists.");
  };

  const plateMutation = useMutation({
    mutationFn: updatePlate,
    onError: (error: ParkPlateUpdatePOSTErrorResponse) => {
      notify({
        severity: "error",
        message: getErrorMessage(error),
      });
      console.error("Error updating receipt plate: ", error);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["receipt"] });
    },
  });

  if (isPending || !receipt) {
    return <ReceiptScreenSkeleton />;
  }

  const totalPaid = formatToDollars(receipt.amountPaid);
  const isFlexPass = receipt.receiptType.toLowerCase().includes("flex");
  const disablePlateSwitch = receipt.blockPlateSwitch || parsedAllowPlateSwitchUntil < new Date();

  const handlePlateSwitch = async (newPlate: string) => {
    setIsUpdatingPlate(true);
    try {
      if (newPlate) {
        await plateMutation.mutateAsync({ newPlate, receiptID });
      }
    } catch (error) {
      console.error("Error updating plate:", error);
    } finally {
      setIsUpdatingPlate(false);
      setIsPlateModalOpen(false);
    }
  };

  const openContactModal = (mode: "email" | "phone") => {
    setModalMode(mode);
    setIsContactModalOpen(true);
  };

  const handleContactConfirm = (contactInput: string) => {
    if (!receiptID) return;
    if (modalMode === "email") {
      sendReceiptByEmail({ receiptID, email: contactInput });
    } else if (modalMode === "phone") {
      sendReceiptByPhoneNumber({ receiptID, phone: contactInput });
    }
    setIsContactModalOpen(false);
    setModalMode(null);
  };

  const humanizeFlexParkName = (type: ReceiptType): string => {
    if (ReceiptType.FlexPassPurchase === type) {
      return "Flex Park Purchase";
    }
    return type;
  };

  return (
    <Screen>
      {modalMode && (
        <ContactModal
          isOpen={isContactModalOpen}
          handleClose={() => {
            setIsContactModalOpen(false);
            setModalMode(null);
          }}
          mode={modalMode}
          handleConfirm={handleContactConfirm}
        />
      )}

      <PageTitle title={formattedDate} />

      {isPlateModalOpen && (
        <UpdatePlateModal
          isOpen={isPlateModalOpen}
          licensePlate={receipt.plate}
          onClose={() => setIsPlateModalOpen(false)}
          onConfirm={handlePlateSwitch}
          loading={isUpdatingPlate}
        />
      )}

      <VStack>
        <VStack className="gap-[32px]">
          {receipt.accessCode && (
            <Card className="py-[32px] px-[20px]">
              <Box className="items-center justify-center gap-[20px]">
                <Box className="gap-[20px] items-center justify-center">
                  <Heading className="font-bold text-[16px] leading-[22px] text-center text-typography-900">
                    <T _str="Door Access Code" />
                  </Heading>

                  <Text className="w-[85%] text-center text-typography-900 font-normal text-[14px] leading-[20px] tracking-0">
                    <T _str="Please save the following code. It will be required to gain access to this facility." />
                  </Text>
                </Box>
                <Text className="font-bold text-[48px] leading-[100%] text-center text-typography-900">{receipt.accessCode}</Text>
              </Box>
            </Card>
          )}

          <Box className="flex flex-col gap-[12px]">
            <Heading className="text-typography-900 text-[16px] font-[700] leading-[22px] align-middle">
              <T _str="Session Details" />
            </Heading>
            <VStack className="gap-[6px]">
              <DetailRow label={t("Location:")} value={receipt.locationName} />
              <DetailRow label={t("Date:")} value={formattedDate} />
            </VStack>
          </Box>

          {receipt.plate && (
            <Section title={t("Vehicle")}>
              <Box className="gap-[12px]">
                <DetailRow label={t("License Plate Number:")} value={receipt.plate} />
                <AppButton
                  disabled={disablePlateSwitch}
                  icon={Car}
                  onPress={() => setIsPlateModalOpen(true)}
                  title={t("Incorrect License Plate?")}
                  variant="primary"
                />
              </Box>
            </Section>
          )}

          <Section title={t("Parking Pass Information")}>
            {isFlexPass && <DetailRow label={t("Pass Type:")} value={humanizeFlexParkName(receipt.receiptType)} />}
            <DetailRow label={t("Lot:")} value={receipt.lotName} />
            <DetailRow label={t(isFlexPass ? "Purchase Time" : "Activation Time:")} value={formattedStartTime} />
            {!isFlexPass && <DetailRow label={t("End Time:")} value={formattedEndTime} />}
          </Section>

          <Section title={t("Payment Details")}>
            <DetailRow label={t("Invoice Number:")} value={receipt.invoiceID} />
            <DetailRow label={t("Payment Method:")} value={receipt.paymentDescription} />
            {receipt.discount > 0 && <DetailRow label={t("Promotion:")} value={promotionCopy(receipt)} />}
            <DetailRow label={t("Total Paid:")} value={totalPaid} />
          </Section>

          <VStack className="gap-[20px]">
            <AppButton
              onPress={() => openContactModal("phone")}
              title={t("Send Receipt via Text")}
              variant="primary"
              icon={SpeechBubble}
              iconPosition="before"
              loading={sendReceiptByPhoneNumberIsPending}
            />
            <AppButton
              onPress={() => openContactModal("email")}
              title={t("Send Receipt via Email")}
              variant="primary"
              icon={Mail}
              iconPosition="before"
              loading={sendReceiptByEmailIsPending}
            />
          </VStack>
        </VStack>
        {receipt.passID && (
          <VStack className="items-center justify-center gap-1 mt-8">
            <Text className="font-[700] text-[12px] text-typography-900 mb-1">For customer service</Text>
            <QRCode value={prettyPassID(receipt.passID)} color={skin.theme.light.typography[900]} size={80} />
            <Text className="font-[400] text-[12px] text-typography-900 mt-1">{prettyPassID(receipt.passID)}</Text>
          </VStack>
        )}
      </VStack>
    </Screen>
  );

  function promotionCopy(receipt: ReceiptMini) {
    const discount = formatToDollars(receipt.discount);

    if (receipt.promotionUserFacingRedeemCopy) {
      return `${receipt.promotionUserFacingRedeemCopy} (-${discount})`;
    }
    return receipt.isAutoApplyPromo || !receipt.promoCode ? `-${discount}` : `${receipt.promoCode} (-${formatToDollars(receipt.discount)})`;
  }
};

export default Receipt;

const ReceiptScreenSkeleton = () => {
  return (
    <Screen>
      <VStack className="gap-[32px]">
        <Box className="items-center">
          <Skeleton variant="rounded" className="h-[20px] w-[70%]" />
        </Box>

        {/* Session Details */}
        <VStack className="gap-[12px]">
          <SkeletonText className="h-[16px] w-[40%]" />
          <HStack className="justify-between">
            <SkeletonText className="h-[14px] w-[30%]" />
            <SkeletonText className="h-[14px] w-[40%]" />
          </HStack>
          <HStack className="justify-between">
            <SkeletonText className="h-[14px] w-[30%]" />
            <SkeletonText className="h-[14px] w-[40%]" />
          </HStack>
        </VStack>

        <Divider className="bg-outline-300" />

        {/* Vehicle information */}
        <VStack className="gap-[12px]">
          <SkeletonText className="h-[16px] w-[40%]" />
          <HStack className="justify-between">
            <SkeletonText className="h-[14px] w-[50%]" />
            <SkeletonText className="h-[14px] w-[30%]" />
          </HStack>
          <Skeleton variant="rounded" className="h-[52px] w-full mt-2" />
        </VStack>

        <Divider className="bg-outline-300" />

        {/* Session Information */}
        <VStack className="gap-[12px]">
          <SkeletonText className="h-[16px] w-[40%]" />
          <HStack className="justify-between">
            <SkeletonText className="h-[14px] w-[30%]" />
            <SkeletonText className="h-[14px] w-[40%]" />
          </HStack>
          <HStack className="justify-between">
            <SkeletonText className="h-[14px] w-[30%]" />
            <SkeletonText className="h-[14px] w-[40%]" />
          </HStack>
          <HStack className="justify-between">
            <SkeletonText className="h-[14px] w-[30%]" />
            <SkeletonText className="h-[14px] w-[40%]" />
          </HStack>
        </VStack>

        <Divider className="bg-outline-300" />

        {/* Payment Details */}
        <VStack className="gap-[12px]">
          <SkeletonText className="h-[16px] w-[40%]" />
          <HStack className="justify-between">
            <SkeletonText className="h-[14px] w-[30%]" />
            <SkeletonText className="h-[14px] w-[40%]" />
          </HStack>
          <HStack className="justify-between">
            <SkeletonText className="h-[14px] w-[30%]" />
            <SkeletonText className="h-[14px] w-[40%]" />
          </HStack>
          <HStack className="justify-between">
            <SkeletonText className="h-[14px] w-[30%]" />
            <SkeletonText className="h-[14px] w-[40%]" />
          </HStack>
          <HStack className="justify-between">
            <SkeletonText className="h-[14px] w-[30%]" />
            <SkeletonText className="h-[14px] w-[40%]" />
          </HStack>
        </VStack>

        {/* Buttons */}
        <VStack className="gap-[20px]">
          <Skeleton variant="rounded" className="h-[52px] w-full" />
          <Skeleton variant="rounded" className="h-[52px] w-full" />
        </VStack>
      </VStack>
    </Screen>
  );
};
